import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Search, Plus, Minus, Trash2, MessageCircle, PlusCircle, ScanLine } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { rupiah } from "@/lib/format";
import { Loading, EmptyState, Button, Card } from "@/components/common";
import BarcodeScanner from "@/components/BarcodeScanner";

export default function POS() {
  const { branch, isManagement } = useAuth();
  const nav = useNavigate();
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [scan, setScan] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, c] = await Promise.all([
          api.get("/products", { active_only: true }),
          api.get("/categories").catch(() => []),
        ]);
        setProducts(p);
        setCategories(c);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (cat === "__display__") { if (!p.is_display) return false; }
      else if (cat && p.category !== cat) return false;
      const hay = `${p.name} ${p.sku || ""} ${(p.variants || []).map((v) => v.sku || "").join(" ")}`.toLowerCase();
      if (q && !hay.includes(q.toLowerCase())) return false;
      return true;
    });
  }, [products, q, cat]);

  const qtyOf = (id) => cart.lines.filter((l) => l.product_id === id).reduce((t, l) => t + l.qty, 0);

  // Barcode / HID scanner: ketik atau scan SKU lalu Enter → langsung masuk keranjang
  const scanValue = (value) => {
    const n = value.trim().toLowerCase();
    if (!n) return;
    for (const p of products) {
      const variant = (p.variants || []).find((v) => (v.sku || "").toLowerCase() === n && v.is_active !== false);
      if (variant) { cart.add(p, 1, variant); toast.success(`${p.name} · ${variant.name} ditambahkan`); setQ(""); return; }
      if ((p.sku || "").toLowerCase() === n) {
        const active = (p.variants || []).filter((v) => v.is_active !== false);
        if (active.length) { toast.message(`${p.name} punya varian — pilih varian`); return; }
        cart.add(p); toast.success(`${p.name} ditambahkan`); setQ(""); return;
      }
    }
    // tidak cocok SKU → biarkan sebagai filter pencarian
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Kasir</h1>
          <p className="text-sm text-slate-500">{branch?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {isManagement && (
            <Button variant="outline" onClick={() => nav("/products")} data-testid="pos-add-product"><PlusCircle size={16} /> Tambah Produk</Button>
          )}
          <Button variant="outline" onClick={() => nav("/whatsapp")} data-testid="pos-wa-button"><MessageCircle size={16} /> WhatsApp</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-6 h-[calc(100vh-12rem)] lg:h-[calc(100vh-13rem)] min-h-0">
        <div className="flex flex-col min-h-0 min-w-0">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") scanValue(q); }}
              placeholder="Cari produk / scan barcode (tekan Enter)..."
              data-testid="pos-search"
              className="w-full h-11 pl-9 pr-24 rounded-xl border border-slate-300 bg-white outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            <button onClick={() => setScan(true)} data-testid="pos-scan-btn"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary hover:text-white transition">
              <ScanLine size={15} /> Scan
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
            <button onClick={() => setCat("")} className={`px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap ${!cat ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>Semua</button>
            <button onClick={() => setCat("__display__")} data-testid="pos-cat-display" className={`px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap ${cat === "__display__" ? "bg-primary text-white" : "bg-emerald-100 text-emerald-700"}`}>🌼 Produk Display / Ready Stock</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCat(c.name)} className={`px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap ${cat === c.name ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{c.name}</button>
            ))}
          </div>

          {loading ? (
            <Loading />
          ) : !filtered.length ? (
            <EmptyState icon={ShoppingCart} title="Tidak ada produk" message="Tambahkan produk lewat menu Produk." />
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {filtered.map((p) => {
                  const hasVariants = (p.variants || []).filter((v) => v.is_active !== false).length > 0;
                  const n = qtyOf(p.id);
                  return (
                    <div
                      key={p.id}
                      data-testid={`product-${p.sku || p.id}`}
                      className={`text-left bg-white rounded-2xl border p-3 transition flex flex-col ${n > 0 ? "border-primary" : "border-slate-200"}`}
                    >
                      <div
                        role={hasVariants ? undefined : "button"}
                        tabIndex={hasVariants ? undefined : 0}
                        onClick={() => !hasVariants && cart.add(p)}
                        className={!hasVariants ? "cursor-pointer" : ""}
                      >
                        <div className="relative aspect-[3/4] rounded-xl bg-slate-100 mb-2 overflow-hidden flex items-center justify-center">
                          {p.photo_file_id || p.image_url ? (
                            <img src={p.photo_file_id ? fileUrl(p.photo_file_id) : p.image_url} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-3xl">💐</span>
                          )}
                          {n > 0 && (
                            <span className="absolute top-1.5 right-1.5 min-w-6 h-6 px-1.5 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center" data-testid={`product-qty-${p.id}`}>{n}</span>
                          )}
                        </div>
                        <div className="font-medium text-sm text-slate-800 line-clamp-2 leading-tight">{p.name}</div>
                        <div className="text-primary font-bold text-sm mt-1">{rupiah(p.price)}</div>
                      </div>
                      {hasVariants ? (
                        <div className="mt-2">
                          <div className="text-[11px] text-slate-400 mb-1">Pilih varian untuk menambah:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {p.variants.filter((v) => v.is_active !== false).map((v) => (
                              <button
                                key={v.id}
                                onClick={() => cart.add(p, 1, v)}
                                data-testid={`variant-${v.id}`}
                                className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary hover:text-white transition"
                              >
                                <Plus size={13} /> {v.name}
                                {(v.effective_price ?? v.price) ? <span className="opacity-70">· {rupiah(v.effective_price ?? v.price)}</span> : null}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => cart.add(p)}
                          data-testid={`add-${p.sku || p.id}`}
                          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary hover:text-white transition"
                        >
                          <Plus size={15} /> Tambah
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <Card className="flex flex-col min-h-0 min-w-0 overflow-hidden">
          <div className="shrink-0 px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ShoppingCart size={18} /> Keranjang</h2>
            {cart.lines.length > 0 && <button onClick={() => cart.clear()} className="text-xs text-rose-600" data-testid="pos-clear-cart">Kosongkan</button>}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {!cart.lines.length ? (
              <div className="text-center text-slate-400 text-sm py-16">Keranjang kosong</div>
            ) : (
              <div className="space-y-2">
                {cart.lines.map((i) => (
                  <div key={i.key} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2.5" data-testid={`cart-item-${i.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{i.name}</div>
                      <div className="text-xs text-slate-400 truncate tabular-nums">{rupiah(i.price)} × {i.qty} = <span className="font-medium text-slate-600">{rupiah(i.price * i.qty)}</span></div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => cart.setQty(i.key, i.qty - 1)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><Minus size={13} /></button>
                      <span className="w-7 text-center text-sm font-medium tabular-nums shrink-0">{i.qty}</span>
                      <button onClick={() => cart.setQty(i.key, i.qty + 1)} className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><Plus size={13} /></button>
                      <button onClick={() => cart.setQty(i.key, 0)} className="w-7 h-7 rounded-lg text-rose-500 flex items-center justify-center shrink-0"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="shrink-0 sticky bottom-0 bg-white border-t border-slate-100 p-4 pb-5">
            <div className="flex justify-between items-center gap-2 mb-3">
              <span className="text-slate-500 shrink-0">Total</span>
              <span className="text-2xl font-bold text-slate-800 tabular-nums truncate text-right" data-testid="cart-total">{rupiah(cart.subtotal)}</span>
            </div>
            <Button className="w-full" size="lg" disabled={!cart.lines.length} onClick={() => nav("/checkout")} data-testid="checkout-btn">
              Lanjut ke Checkout
            </Button>
          </div>
        </Card>
      </div>
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onScanned={(v) => scanValue(v)} title="Scan Barcode Produk" />
    </>
  );
}
